// ─────────────────────────────────────────────────────────────
// JerryMarkerStore — persists Group and GroupSustain markers
// per-project in localStorage, keyed by project UUID.
// ─────────────────────────────────────────────────────────────

import {Notifier, Subscription} from "@opendaw/lib-std"
import {JerryLaneData, JerryLaneType, JerryMarker} from "./JerryMarkerTypes"

const STORAGE_PREFIX = "opendaw-jerry-markers-"

class JerryMarkerStore {
    readonly #changeNotifier = new Notifier<void>()

    #projectUUID: string | null = null
    #data: JerryLaneData = {groupMarkers: [], groupSustainMarkers: [], sectionMarkers: []}

    // ── Project lifecycle ──────────────────────────────────────

    load(projectUUID: string): void {
        this.#projectUUID = projectUUID
        const raw = localStorage.getItem(STORAGE_PREFIX + projectUUID)
        if (raw) {
            try {
                this.#data = JSON.parse(raw)
            } catch {
                this.#data = {groupMarkers: [], groupSustainMarkers: [], sectionMarkers: []}
            }
        } else {
            this.#data = {groupMarkers: [], groupSustainMarkers: [], sectionMarkers: []}
        }
        this.#changeNotifier.notify()
    }

    unload(): void {
        this.#projectUUID = null
        this.#data = {groupMarkers: [], groupSustainMarkers: [], sectionMarkers: []}
        this.#changeNotifier.notify()
    }

    #save(): void {
        if (!this.#projectUUID) {return}
        localStorage.setItem(STORAGE_PREFIX + this.#projectUUID, JSON.stringify(this.#data))
    }

    // ── Read ───────────────────────────────────────────────────

    getMarkers(lane: JerryLaneType): ReadonlyArray<JerryMarker> {
        if (lane === "group") return this.#data.groupMarkers
        if (lane === "groupSustain") return this.#data.groupSustainMarkers
        return this.#data.sectionMarkers
    }

    // ── Write ──────────────────────────────────────────────────

    addMarker(lane: JerryLaneType, position: number, label: string): void {
        const marker: JerryMarker = {id: crypto.randomUUID(), position, label}
        const list = this.#listFor(lane)
        list.push(marker)
        list.sort((a, b) => a.position - b.position)
        this.#save()
        this.#changeNotifier.notify()
    }

    moveMarker(lane: JerryLaneType, id: string, position: number): void {
        const list = this.#listFor(lane)
        const marker = list.find(m => m.id === id)
        if (!marker) {return}
        marker.position = position
        list.sort((a, b) => a.position - b.position)
        this.#save()
        this.#changeNotifier.notify()
    }

    renameMarker(lane: JerryLaneType, id: string, label: string): void {
        const list = this.#listFor(lane)
        const marker = list.find(m => m.id === id)
        if (!marker) {return}
        marker.label = label
        this.#save()
        this.#changeNotifier.notify()
    }

    deleteMarker(lane: JerryLaneType, id: string): void {
        if (lane === "group") {
            this.#data.groupMarkers = this.#data.groupMarkers.filter(m => m.id !== id)
        } else if (lane === "groupSustain") {
            this.#data.groupSustainMarkers = this.#data.groupSustainMarkers.filter(m => m.id !== id)
        } else {
            this.#data.sectionMarkers = this.#data.sectionMarkers.filter(m => m.id !== id)
        }
        this.#save()
        this.#changeNotifier.notify()
    }

    #listFor(lane: JerryLaneType): JerryMarker[] {
        if (lane === "group") return this.#data.groupMarkers
        if (lane === "groupSustain") return this.#data.groupSustainMarkers
        return this.#data.sectionMarkers
    }

    // ── Reactivity ─────────────────────────────────────────────

    subscribe(observer: () => void): Subscription {
        return this.#changeNotifier.subscribe(observer)
    }

    // ── Export helper (for future Cloudflare integration) ──────

    exportForProject(): JerryLaneData {
        return JSON.parse(JSON.stringify(this.#data))
    }
}

// Singleton — one store for the whole app, just like OpenDAW's engine state
export const jerryMarkerStore = new JerryMarkerStore()
